import React, { useState, useRef } from 'react';
import type { FormikProps, FormikHelpers } from 'formik';
import { Formik, Form } from 'formik';

import { IconButton, Card } from '@mui/material';

import { setTimeout } from 'timers';

import * as Yup from 'yup';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Icon from 'src/components/ui/Icon';
import Typography from 'src/components/ui/Typography';
import TextField from 'src/components/ui/TextField';
import TinyPreviewCard from 'src/components/ui/TinyPreviewCard/TinyPreviewCard';
import StoryReactionModal from 'src/components/news/StoryReactionModal';
import Image from 'src/components/ui/Image';

import { colours } from 'src/constants/colours';
import { commonMultilineMaxLength } from 'src/constants/misc';
import { routePathAd, routePathNewsPost } from 'src/constants/router';

import useUser from 'src/hooks/useUser';
import useLoginModal from 'src/hooks/useLoginModal';
import useModalOpenState from 'src/hooks/useModalOpenState';
import useAppRouter from 'src/hooks/useAppRouter';
import useAlerts from 'src/hooks/useAlerts';
import { notBlankSpaceRegEx } from 'src/constants/regexes';
import type { Ad, Medium, NewsCategory, StoriesQuery } from 'src/types/__generated__/graphql';
import { DataType, Reaction } from 'src/types/__generated__/graphql';
import { createUsersChannel } from 'src/utils/sendbirdUtils';
import { useSendSharedContentMessageMutation } from 'src/hooks/__generated__/queries';
import formatPrice from 'src/utils/formatPrice';
import { styledComponent } from 'src/utils/styled';
import { AnalyticsEvent } from 'src/utils/analytics';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';

const BottomStack = styledComponent(Box)`
    z-index: 1000 !important;
    background: linear-gradient(0deg, rgba(19, 46, 72, 0.6) 0%, rgba(19, 46, 72, 0) 100%);
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
`;

const FloatingText = styledComponent(Box)`
    text-align: center;
    h2 {
        color: ${colours.white.main};
    }
`;

const StoryReactions = styledComponent(Box)`
    margin-bottom: 3.25rem;
    h6 {
        color: ${colours.white.main};
    }
`;

const ReactionsContainer = styledComponent(Box)`
    margin-top: 1.5rem;
    width: 14rem;
    height: 10rem;
    display: grid;
    grid-template-columns: repeat(3, minmax(3rem, 1fr));
    img {
        background-color: transparent;
    }
`;

const StyledCard = styledComponent(Card)`
    max-width: 20.5rem;
    position: relative;
    margin: 0 auto 1rem auto;
    cursor: pointer;
    border-radius: 0.5rem;
`;

const MessageBarContainer = styledComponent(Box)`
    max-width: 30rem;
    width: 80%;
    margin: auto;
`;

const MessageBar = styledComponent(TextField)`
    .MuiInputBase-root {
        background-color: transparent;
        color: ${colours.white.main};
    }
    .MuiInputLabel-root {
        color: ${colours.white.main};
    }
`;

const StyledBox = styledComponent(Box)`
    background: ${colours.blackOpacity.O72};
    opacity: 0.8;
    width: 11.2rem;
    padding: 0.25rem 0.75rem 0.25rem 0.75rem;
    border-radius: 0.25rem;
    text-align: center;
    position: absolute;
    color: ${colours.white.main};
    top: -20%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const STORY_REACTIONS = [
    Reaction.Wow,
    Reaction.Laughing,
    Reaction.Party,
    Reaction.HeartEyes,
    Reaction.StarEyes,
    Reaction.Sad,
    Reaction.Celebrate,
    Reaction.Fire,
    Reaction.Applause,
];

interface Props {
    story: ArrayElement<NonNullable<StoriesQuery['stories']>>;
    setIsPaused?: (isPaused: boolean) => void;
    setTwoSecondStory?: (setTwoSecondStory: boolean) => void;
}

interface FormValues {
    message: string;
}

const StoryFooter: React.FC<Props> = ({ story, setIsPaused }) => {
    const router = useAppRouter();
    const currentUser = useUser();
    const { t, i18n } = useTranslation(['commmon', 'news']);
    const { addAlert } = useAlerts();
    const [afterSendMessage, setAfterSendMessage] = useState<string>();
    const [afterSendReaction, setAfterSendReaction] = useState<string>();
    const [displayFor2second, setDisplayFor2second] = useState<boolean>(true);
    const [showStoryReactions, setShowStoryReactions] = useState<boolean>(false);
    const { createAuthenticatedHandler } = useLoginModal();
    const formikRef = useRef<FormikProps<FormValues>>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [sendSharedContentMessageMutation] = useSendSharedContentMessageMutation();
    const { logEventDynamic } = useDynamicLogEvents();

    const { props: storyReactionModalProps, open: storyReactionModal } = useModalOpenState();

    const handleLinkableCardClick = async () => {
        await router.push(story.ad ? `${routePathAd}/${story.ad.id}` : `${routePathNewsPost}/${story.newsPost?.id}`);
    };

    const handleOnCancel = () => {
        storyReactionModalProps.onClose();
        setIsPaused?.(false);
    };

    const handleClickReactNumberIcon = () => {
        storyReactionModal();
        setIsPaused?.(true);
    };

    const handleClickShowReaction = () => {
        setIsPaused?.(true);
        setShowStoryReactions(true);
    };
    const isAdminOfCompanyStory =
        currentUser?.isAdminOfAffiliatedCompany && currentUser.affiliatedCompany?.id === story.owner.company?.id;

    const sendMessage = async ({ message, reaction }: { message?: string; reaction?: Reaction }) => {
        if (currentUser && (message || reaction)) {
            if (
                currentUser.sendbirdUserId &&
                (story.owner.user?.sendbirdUserId || story.owner.company?.contactUser?.sendbirdUserId)
            ) {
                const contactedSendbirdUserId = (story.owner.user?.sendbirdUserId ??
                    story.owner.company?.contactUser?.sendbirdUserId) as string;
                const channel = await createUsersChannel(currentUser.sendbirdUserId, [contactedSendbirdUserId]);
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                sendSharedContentMessageMutation({
                    variables: {
                        channelUrl: channel.url,
                        message,
                        data: {
                            type: DataType.Story,
                            payload: {
                                id: story.id,
                                reaction,
                            },
                        },
                    },
                });
                logEventDynamic(AnalyticsEvent.SendMessage);
            }
        }
    };

    const handleForm = (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
        try {
            setTimeout(() => {
                setDisplayFor2second(false);
                setAfterSendMessage('');
            }, 2000);
            inputRef.current?.blur();
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            sendMessage({
                message: values.message,
            });
            resetForm();
            setIsPaused?.(false);
            setAfterSendMessage(story.id);
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: (error as Error).message,
            });
        }
        setShowStoryReactions(false);
    };

    const handleReactionClick = (reaction: Reaction) => {
        setDisplayFor2second(true);
        try {
            setTimeout(() => {
                setDisplayFor2second(false);
                setAfterSendReaction('');
            }, 2000);
            inputRef.current?.blur();
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            sendMessage({
                reaction,
            });
            setIsPaused?.(false);
            setAfterSendReaction(story.id);
            formikRef.current?.resetForm();
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: (error as Error).message,
            });
        }
        setShowStoryReactions(false);
    };

    const handleContainerBlur = (value: string) => {
        if (!value) {
            formikRef.current?.resetForm();
        }
        setShowStoryReactions(false);
    };

    const floatingText = (
        <FloatingText width="100%" height="5.75rem" padding="2rem 0">
            <Typography data-target="story-modal-text" data-target-id="story-modal-message" variant="h2" serif>
                {story.floatingText}
            </Typography>
        </FloatingText>
    );

    const price = formatPrice({
        price: Number(story.ad?.price ?? story.ad?.rent),
        lang: i18n.language,
    });

    const linkableContent =
        story.ad || story.newsPost ? (
            <StyledCard onClick={handleLinkableCardClick}>
                {story.ad && (
                    <TinyPreviewCard
                        iconName="AdIcon"
                        ad={story.ad as Ad}
                        type="ad"
                        media={story.ad.media as Medium[]}
                        subtitle={price}
                        title={t(`ad:propertyTypes.${story.ad.propertyType}`)}
                    />
                )}
                {story.newsPost && (
                    <TinyPreviewCard
                        iconName="NewsIcon"
                        type="newsPost"
                        tag={story.newsPost.featuredCategory as NewsCategory}
                        media={story.newsPost.media as Medium[]}
                        subtitle={story.newsPost.title as string}
                    />
                )}
            </StyledCard>
        ) : null;

    const storyReactions = (
        <StoryReactions>
            <Box flexDirection="column" display="flex" alignItems="center">
                <Typography variant="h6">{t('news:quickReactions')}</Typography>
                <ReactionsContainer>
                    {STORY_REACTIONS.map((reaction) => (
                        <Image
                            key={String(reaction)}
                            data-target-id={`${reaction}-reaction`}
                            src={`${process.env.NEXT_PUBLIC_SITE_URL}/assets/images/stories/reactions/woopy-${reaction}.png`}
                            alt={`woopy-${reaction}`}
                            height={100}
                            width={100}
                            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                            onClick={() => handleReactionClick(reaction)}
                            onMouseDown={(event) => event.preventDefault()}
                            priority
                        />
                    ))}
                </ReactionsContainer>
            </Box>
        </StoryReactions>
    );

    const schema = Yup.object().shape({
        message: Yup.string()
            .matches(notBlankSpaceRegEx, t('common:errors:atLeastOneProperty'))
            .nullable()
            .required(t('common:errors:atLeastOneProperty'))
            .min(1)
            .max(commonMultilineMaxLength, t('common:errors.tooLong', { number: commonMultilineMaxLength })),
    });

    const sendMessageBar = (
        <Formik
            initialValues={{
                message: '',
            }}
            innerRef={formikRef}
            validationSchema={schema}
            onSubmit={handleForm}
        >
            {({ handleSubmit, values }: FormikProps<FormValues>) => (
                <Form autoComplete="off">
                    {((currentUser?.id !== story.owner.user?.id && !isAdminOfCompanyStory) ||
                        currentUser?.id === undefined) && (
                        <MessageBarContainer
                            data-target-id="story-message-bar-container"
                            onFocus={() => setShowStoryReactions(true)}
                            onBlur={() => handleContainerBlur(values.message)}
                        >
                            <MessageBar
                                onClick={() => createAuthenticatedHandler(handleClickShowReaction)}
                                data-target-id="story-message-bar"
                                inputRef={inputRef}
                                name="message"
                                disabled={!currentUser}
                                label={t('news:sendMessage')}
                                onKeyPress={(e) => {
                                    if (e.code === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSubmit();
                                        inputRef.current?.blur();
                                        setDisplayFor2second(true);
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <Box display="flex" flexDirection="row">
                                            <Icon
                                                onClick={() => {
                                                    handleSubmit();
                                                    setDisplayFor2second(true);
                                                }}
                                                name="Send"
                                                style={{ stroke: colours.white.main }}
                                            />
                                        </Box>
                                    ),
                                }}
                            />
                        </MessageBarContainer>
                    )}
                </Form>
            )}
        </Formik>
    );

    const numberOfReactions = story.numberOfReactions;

    return (
        <BottomStack>
            {afterSendMessage === story.id && displayFor2second && (
                <StyledBox>
                    <Typography>{t('news:messageSent')}</Typography>
                </StyledBox>
            )}
            {afterSendReaction === story.id && displayFor2second && (
                <StyledBox>
                    <Typography>{t('news:reactionSent')}</Typography>
                </StyledBox>
            )}

            {story.floatingText && floatingText}
            {(story.ad || story.newsPost) && linkableContent}
            {showStoryReactions && storyReactions}
            {sendMessageBar}
            {(currentUser?.id === story.owner.user?.id || isAdminOfCompanyStory) && currentUser?.id !== undefined && (
                <Box display="flex" flexDirection="row" justifyContent="flex-end">
                    <IconButton onClick={handleClickReactNumberIcon}>
                        <Box display="flex" flexDirection="column" justifyContent="center">
                            <Box padding="0.875rem">
                                <Icon name="Reaction" size="2.5rem" />
                            </Box>
                            <Box padding="0.875rem" mt="-3.7rem">
                                <Typography variant="subtitle2" color="white">
                                    {numberOfReactions}
                                </Typography>
                            </Box>
                        </Box>
                    </IconButton>
                </Box>
            )}
            {storyReactionModalProps.open && (
                <StoryReactionModal story={story} onCancel={handleOnCancel} {...storyReactionModalProps} />
            )}
        </BottomStack>
    );
};

export default StoryFooter;
